import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input, Select } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { confirmCost } from "@/lib/confirmCost";
import { pollJob } from "@/lib/api";
import { Sparkles, Search, Zap, Database, Radar, CheckCircle2, AlertCircle, Download, Briefcase, Calculator, Landmark, Users, GitMerge, Target, Globe, Leaf } from "lucide-react";
import { ATECO_CATEGORIES, PROVINCE } from "@/lib/ateco";
import { Link } from "react-router-dom";

const STRATEGIES = [
  { id: "buy_and_build", name: "Buy & Build", desc: "Platform + add-on acquisitions" },
  { id: "distressed", name: "Distressed", desc: "Undervalued, restructuring plays" },
  { id: "undervalued", name: "Undervalued", desc: "Mispriced assets with upside" },
  { id: "consolidation", name: "Consolidation", desc: "Sector roll-up" },
];

const CATEGORIES = [
  { id: "", name: "Standard Discovery", desc: "Buy & build, consolidation, undervalued", icon: Target },
  { id: "extraordinary_finance", name: "Extraordinary Finance", desc: "M&A, mergers, acquisitions, restructurings", icon: GitMerge },
  { id: "generational_succession", name: "Generational Succession", desc: "Family-owned businesses with imminent succession", icon: Users },
  { id: "investor_search", name: "Investor Search", desc: "Active PE / VC funds — Italy & Europe", icon: Landmark },
  { id: "internationalization", name: "Internationalization", desc: "Companies expanding abroad or with cross-border potential", icon: Globe },
];

const TABS = [
  { id: "discovery", label: "AI Deep Discovery", icon: Sparkles, desc: "Multi-LLM (Claude + GPT-5.2)" },
  { id: "registro", label: "Registro Imprese", icon: Database, desc: "Live Openapi.com" },
  { id: "scan", label: "Database Scan", icon: Radar, desc: "Filter existing targets" },
];

export default function Origin() {
  const [tab, setTab] = useState("discovery");
  const [filters, setFilters] = useState({
    sector: "", country: "Italy", min_revenue: "", max_revenue: "",
    min_ebitda_margin: "", strategy: "buy_and_build", theme: "", category: "",
    provincia: "", ateco: "", denominazione: "", dipendenti_min: "", dipendenti_max: "",
    no_esg_only: false,
  });
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [registroLoading, setRegistroLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [registro, setRegistro] = useState(null);

  // Parallel multi-category state — key: category id, value: {status:'loading'|'ready'|'error', data|error}
  const [multi, setMulti] = useState({});
  const [multiRunning, setMultiRunning] = useState(false);
  const [bgEnrichmentOn, setBgEnrichmentOn] = useState(true);

  React.useEffect(() => {
    endpoints.getEnrichmentSetting()
      .then((r) => setBgEnrichmentOn(!!r.data.background_enrichment_enabled))
      .catch(() => {});
  }, []);


  const runScout = async () => {
    setLoading(true);
    try {
      const body = {
        strategy: filters.strategy,
        sector: filters.sector || null,
        country: filters.country || null,
        min_revenue: filters.min_revenue ? Number(filters.min_revenue) * 1e6 : null,
        max_revenue: filters.max_revenue ? Number(filters.max_revenue) * 1e6 : null,
        min_ebitda_margin: filters.min_ebitda_margin ? Number(filters.min_ebitda_margin) : null,
      };
      const r = await endpoints.scout(body);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const runDiscovery = async () => {
    const catName = CATEGORIES.find((c) => c.id === filters.category)?.name || "Standard Discovery";
    const confirmed = await confirmCost({
      op: "discover", units: 1,
      title: `Run AI Deep Discovery — ${catName}?`,
      items: [`Sector: ${filters.sector || "—"}`, `Country: ${filters.country || "Italy"}`, `Revenue band: ${filters.min_revenue || "—"}–${filters.max_revenue || "—"} €M`],
      llm_calls: 2,
      web_calls: 1,
      estimated_seconds: 60,
    });
    if (!confirmed) return;
    setDiscovering(true);
    setDiscovery(null);
    try {
      const body = {
        country: filters.country || "Italy",
        sector: filters.sector || null,
        revenue_min_mn: filters.min_revenue ? Number(filters.min_revenue) : null,
        revenue_max_mn: filters.max_revenue ? Number(filters.max_revenue) : null,
        strategy: filters.strategy,
        theme: filters.theme || null,
        category: filters.category || null,
        no_esg_only: !!filters.no_esg_only,
      };
      const r = await endpoints.discover(body);
      setDiscovery(r.data);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 502 || st === 504 || e.code === "ECONNABORTED") {
        alert("The search is taking more than 100 seconds: CLARA continues to work in the background. Targets, deals and dossiers will appear in Companies / Deals / Valuations within a few minutes.");
      } else {
        alert("AI Discovery failed: " + (e?.response?.data?.detail || e.message));
      }
    } finally { setDiscovering(false); }
  };

  const runMultiCategory = async () => {
    const cats = ["extraordinary_finance", "generational_succession", "investor_search", "internationalization"];
    const confirmed = await confirmCost({
      op: "discover", units: cats.length,
      title: "Run all 4 origination categories in parallel?",
      items: cats.map((c) => CATEGORIES.find((x) => x.id === c)?.name || c),
      llm_calls: cats.length * 2,
      web_calls: cats.length * 1,
      estimated_seconds: 60,
    });
    if (!confirmed) return;
    setDiscovery(null);
    setMultiRunning(true);
    setMulti(Object.fromEntries(cats.map((c) => [c, { status: "loading" }])));
    try {
      const r = await endpoints.discoverMulti({
        categories: cats,
        country: filters.country || "Italy",
        sector: filters.sector || null,
        revenue_min_mn: filters.min_revenue ? Number(filters.min_revenue) : null,
        revenue_max_mn: filters.max_revenue ? Number(filters.max_revenue) : null,
        strategy: filters.strategy,
        theme: filters.theme || null,
        no_esg_only: !!filters.no_esg_only,
      });
      // Fire-and-forget job: poll every 3s, each category slot fills as soon as it settles
      await pollJob(`/origin/discover-multi/status/${r.data.job_id}`, (snap) => {
        setMulti(Object.fromEntries(cats.map((c) => {
          const p = snap.partial?.[c];
          if (!p || p.status === "loading") return [c, { status: "loading" }];
          if (p.status === "ready") return [c, { status: "ready", data: p }];
          return [c, { status: "error", error: p.error || "Category failed" }];
        })));
      });
    } catch (e) {
      setMulti(Object.fromEntries(cats.map((c) => [c, { status: "error", error: e?.response?.data?.detail || e.message }])));
    } finally {
      setMultiRunning(false);
    }
  };

  const runRegistro = async (doImport = false) => {    setRegistroLoading(true);
    setRegistro(null);
    try {
      const body = {
        denominazione: filters.denominazione || null,
        provincia: filters.provincia || null,
        ateco: filters.ateco || null,
        fatturato_min: filters.min_revenue ? Number(filters.min_revenue) * 1e6 : null,
        fatturato_max: filters.max_revenue ? Number(filters.max_revenue) * 1e6 : null,
        dipendenti_min: filters.dipendenti_min ? Number(filters.dipendenti_min) : null,
        dipendenti_max: filters.dipendenti_max ? Number(filters.dipendenti_max) : null,
        limit: 30,
      };
      const fn = doImport ? endpoints.registroImpreseImport : endpoints.registroImprese;
      const r = await fn(body);
      setRegistro(r.data);
    } catch (e) {
      alert("Registro Imprese search failed: " + (e?.response?.data?.detail || e.message));
    } finally { setRegistroLoading(false); }
  };

  // Scout runs only on explicit click of "Scan Database" button — no auto-run on mount.

  return (
    <div className="p-4 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        eyebrow="CLARA Origin"
        title={<>The <span className="italic text-gold">origination</span> engine.</>}
        description="Find REAL acquisition targets — Italian PMI, European mid-market, family-owned hidden champions. 3 engines: AI Deep Discovery (Claude + GPT-5.2), Registro Imprese live (Openapi.com), Database scan."
        testid="origin-page"
        actions={
          <Link to="/origin/performance">
            <Button variant="outline" data-testid="goto-performance">
              <Zap size={14} /> Performance Dashboard
            </Button>
          </Link>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-3 rounded-md text-left border transition whitespace-nowrap flex items-center gap-2 ${
              tab === t.id
                ? "bg-[#C5A059]/10 border-[#C5A059]/50 text-white"
                : "border-white/5 hover:border-white/20 text-white/60"
            }`}
          >
            <t.icon size={15} className={tab === t.id ? "text-gold" : ""} />
            <div>
              <div className="text-[13px] font-medium">{t.label}</div>
              <div className="text-[10px] text-white/40">{t.desc}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <div className="overline mb-4">Criteria</div>

            <div className="space-y-4">
              {tab === "discovery" && (
                <div>
                  <label className="text-[11px] text-white/50 mb-1 block">Origination category</label>
                  <div className="space-y-1.5">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c.id || "standard"}
                        type="button"
                        onClick={() => setFilters({ ...filters, category: c.id })}
                        data-testid={`category-${c.id || "standard"}`}
                        className={`w-full text-left px-3 py-2 rounded-md text-[12px] border transition-all flex items-start gap-2 ${
                          filters.category === c.id
                            ? "border-[#C5A059]/60 bg-[#C5A059]/10 text-white"
                            : "border-white/5 hover:border-white/20 text-white/70"
                        }`}
                      >
                        <c.icon size={14} className={`mt-0.5 shrink-0 ${filters.category === c.id ? "text-gold" : "text-white/40"}`} />
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-[10px] text-white/40 mt-0.5">{c.desc}</div>
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={runMultiCategory}
                      disabled={multiRunning}
                      data-testid="run-multi-category-btn"
                      className="w-full text-left px-3 py-2 rounded-md text-[12px] border-2 border-dashed border-[#C5A059]/40 hover:border-[#C5A059] hover:bg-[#C5A059]/10 transition-all flex items-center gap-2 text-white/80"
                    >
                      {multiRunning ? <Spinner size={12} /> : <Zap size={13} className="text-gold" />}
                      <div className="flex-1">
                        <div className="font-medium">Run all 4 categories in parallel</div>
                        <div className="text-[10px] text-white/40 mt-0.5">Fires concurrent requests — each category loads independently</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {tab !== "registro" && !(tab === "discovery" && filters.category) && (
                <div>
                  <label className="text-[11px] text-white/50 mb-1 block">Strategy</label>
                  <div className="space-y-1.5">
                    {STRATEGIES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setFilters({ ...filters, strategy: s.id })}
                        data-testid={`strategy-${s.id}`}
                        className={`w-full text-left px-3 py-2 rounded-md text-[12px] border transition-all ${
                          filters.strategy === s.id
                            ? "border-[#C5A059]/50 bg-[#C5A059]/10 text-white"
                            : "border-white/5 hover:border-white/20 text-white/70"
                        }`}
                      >
                        <div className="font-medium">{s.name}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{s.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {tab === "registro" ? (
                <>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Company name <span className="text-white/30">or VAT/fiscal code (11/16 digits)</span></label>
                    <Input placeholder="e.g. Parpas or 15844561009" value={filters.denominazione} onChange={(e) => setFilters({ ...filters, denominazione: e.target.value })} data-testid="registro-denom" />
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Province</label>
                    <Select value={filters.provincia} onChange={(e) => setFilters({ ...filters, provincia: e.target.value })} data-testid="registro-prov">
                      <option value="">Any</option>
                      {PROVINCE.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">ATECO sector</label>
                    <Select value={filters.ateco} onChange={(e) => setFilters({ ...filters, ateco: e.target.value })} data-testid="registro-ateco">
                      <option value="">Any sector</option>
                      {ATECO_CATEGORIES.map((grp) => (
                        <optgroup key={grp.group} label={grp.group}>
                          {grp.items.map((it) => <option key={it.code} value={it.code}>{it.code} — {it.label}</option>)}
                        </optgroup>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Revenue (€ Millions)</label>
                    <div className="flex gap-2">
                      <Input placeholder="Min" type="number" value={filters.min_revenue} onChange={(e) => setFilters({ ...filters, min_revenue: e.target.value })} />
                      <Input placeholder="Max" type="number" value={filters.max_revenue} onChange={(e) => setFilters({ ...filters, max_revenue: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Employees</label>
                    <div className="flex gap-2">
                      <Input placeholder="Min" type="number" value={filters.dipendenti_min} onChange={(e) => setFilters({ ...filters, dipendenti_min: e.target.value })} />
                      <Input placeholder="Max" type="number" value={filters.dipendenti_max} onChange={(e) => setFilters({ ...filters, dipendenti_max: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => runRegistro(false)} disabled={registroLoading} variant="outline" data-testid="registro-search-btn">
                      {registroLoading ? <Spinner size={12} /> : <><Search size={12} /> Search</>}
                    </Button>
                    <Button onClick={() => runRegistro(true)} disabled={registroLoading} data-testid="registro-import-btn">
                      <Download size={12} /> Import
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Sector</label>
                    <Input placeholder="e.g. Precision Mechanics, SaaS, Food" value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} data-testid="origin-sector" />
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Country</label>
                    <Input placeholder="Italy" value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 mb-1 block">Revenue range (€ Millions)</label>
                    <div className="flex gap-2">
                      <Input placeholder="Min" type="number" value={filters.min_revenue} onChange={(e) => setFilters({ ...filters, min_revenue: e.target.value })} />
                      <Input placeholder="Max" type="number" value={filters.max_revenue} onChange={(e) => setFilters({ ...filters, max_revenue: e.target.value })} />
                    </div>
                  </div>
                  {tab === "discovery" && (
                    <div>
                      <label className="text-[11px] text-white/50 mb-1 block">Theme (optional)</label>
                      <Input placeholder="e.g. family succession, carve-out" value={filters.theme} onChange={(e) => setFilters({ ...filters, theme: e.target.value })} />
                    </div>
                  )}
                  {tab === "discovery" && (
                    <label className="flex items-start gap-2 text-[11px] text-white/70 cursor-pointer select-none px-3 py-2.5 rounded-md border border-white/5 hover:border-white/20 transition-all" data-testid="no-esg-filter-row">
                      <input
                        type="checkbox"
                        checked={!!filters.no_esg_only}
                        onChange={(e) => setFilters({ ...filters, no_esg_only: e.target.checked })}
                        className="mt-0.5 accent-[#C5A059]"
                        data-testid="no-esg-filter-checkbox"
                      />
                      <span className="flex-1">
                        <span className="inline-flex items-center gap-1 font-medium text-white">
                          <Leaf size={12} className="text-gold" /> No-ESG filter
                        </span>
                        <span className="block text-[10px] text-white/40 mt-0.5">Only targets without a structured ESG policy — advisory opportunity</span>
                      </span>
                    </label>
                  )}
                  {tab === "scan" && (
                    <div>
                      <label className="text-[11px] text-white/50 mb-1 block">Min EBITDA margin %</label>
                      <Input placeholder="10" type="number" value={filters.min_ebitda_margin} onChange={(e) => setFilters({ ...filters, min_ebitda_margin: e.target.value })} />
                    </div>
                  )}

                  {tab === "discovery" ? (
                    <Button onClick={runDiscovery} disabled={discovering} data-testid="run-discover-btn" className="w-full justify-center">
                      {discovering ? <><Spinner size={13} /> {filters.category === "investor_search" ? "Researching investors…" : "Researching real companies…"}</> : <><Sparkles size={13} /> {CATEGORIES.find((c) => c.id === filters.category)?.id ? CATEGORIES.find((c) => c.id === filters.category).name : "Deep Discovery"}</>}
                    </Button>
                  ) : (
                    <Button onClick={runScout} disabled={loading} data-testid="run-scout-btn" variant="outline" className="w-full justify-center">
                      {loading ? <><Spinner size={13} /> Scanning…</> : <><Radar size={13} /> Scan Database</>}
                    </Button>
                  )}
                </>
              )}

              {tab === "discovery" && (
                <div className="text-[10px] text-white/40 leading-relaxed pt-2 border-t border-white/5">
                  🧠 <strong>Claude Sonnet 4.5</strong> + <strong>GPT-5.2</strong> in parallel. Cross-confirmed results get <span className="text-gold">HIGH confidence</span>. Focus on mid-market, lesser-known European SMEs (€5M–€500M).
                  {filters.category && <div className="mt-1.5 text-gold/80">For every target: company card + <strong>Decision Makers</strong> (Tavily) + PDF dossier generated in the background.</div>}
                  <div className="flex items-start gap-2 mt-3 pt-2 border-t border-white/5" data-testid="bg-enrichment-status-row">
                    <span className="flex-1">
                      <span className="font-medium text-white/80">Background dossier enrichment: <span className={bgEnrichmentOn ? "text-emerald-400" : "text-amber-400"} data-testid="bg-enrichment-status">{bgEnrichmentOn ? "ON" : "OFF"}</span></span>
                      <Link to="/settings?tab=platform" className="block text-gold/80 hover:text-gold mt-0.5 underline-offset-2 hover:underline" data-testid="bg-enrichment-settings-link">Manage in Settings → Platform</Link>
                    </span>
                  </div>
                </div>
              )}
              {tab === "registro" && (
                <div className="text-[10px] text-white/40 leading-relaxed pt-2 border-t border-white/5">
                  💼 Live query to the <strong>Italian Registro Imprese</strong> via Openapi.com. Access filed financial statements, VAT, fiscal code, PEC for any Italian S.p.A./S.r.l.
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 space-y-5">
          {tab === "discovery" && (
            <>
              {Object.keys(multi).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="multi-category-results">
                  {["extraordinary_finance", "generational_succession", "investor_search", "internationalization"].map((cat) => {
                    const meta = CATEGORIES.find((c) => c.id === cat);
                    const state = multi[cat];
                    if (!state) return null;
                    const Icon = meta?.icon || Sparkles;
                    return (
                      <Card key={cat} className="p-4" data-testid={`multi-card-${cat}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={14} className="text-gold" />
                          <div className="font-medium text-[13px]">{meta?.name}</div>
                          {state.status === "loading" && <Badge tone="neutral"><Spinner size={9} /> Researching…</Badge>}
                          {state.status === "queued" && <Badge tone="yellow">Backend processing</Badge>}
                          {state.status === "ready" && <Badge tone="green">{state.data?.inserted_count ?? 0} inserted</Badge>}
                          {state.status === "error" && <Badge tone="red">Error</Badge>}
                        </div>
                        {state.status === "loading" && (
                          <div className="text-[11px] text-white/40 py-3 flex items-center gap-2"><Spinner size={11} /> Claude + GPT-5.2 in parallel…</div>
                        )}
                        {state.status === "queued" && (
                          <div className="text-[11px] text-amber-300/80 py-2">{state.error}</div>
                        )}
                        {state.status === "error" && (
                          <div className="text-[11px] text-red-400 py-2">{state.error}</div>
                        )}
                        {state.status === "ready" && state.data && (
                          <div className="text-[11px] text-white/70 space-y-1">
                            <div>Targets found: <span className="font-mono text-white">{state.data.total_candidates ?? state.data.targets?.length ?? 0}</span></div>
                            <div>Deals created: <span className="font-mono text-white">{state.data.deals_created ?? 0}</span></div>
                            <div>Dossiers queued: <span className="font-mono text-white">{state.data.valuations_queued ?? 0}</span></div>
                            <div className="text-[10px] text-white/40 mt-2 line-clamp-3">{state.data.thesis}</div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              {discovery && (
                <Card>
                  <div className="flex items-start gap-3 mb-4">
                    <Sparkles size={20} className="text-gold mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <div className="overline">Multi-LLM Consensus</div>
                        {discovery.category && <Badge tone="gold" className="normal-case">{CATEGORIES.find((c) => c.id === discovery.category)?.name}</Badge>}
                        <Badge tone="gold">{discovery.inserted_count} {discovery.category === "investor_search" ? "investors" : "companies"}</Badge>
                        <Badge tone="navy">{discovery.deals_created || 0} deals in pipeline</Badge>
                        <Badge tone="green">{discovery.valuations_queued || 0} {discovery.category === "investor_search" ? "investor dossiers" : "valuations"} generating</Badge>
                        {discovery.category && <Badge tone="navy"><Users size={10} /> decision makers hunting</Badge>}
                        <Badge tone="neutral">{(discovery.sources_used || []).join(" + ")}</Badge>
                      </div>
                      <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap mb-3">{discovery.thesis}</div>
                      <div className="flex gap-2 flex-wrap">
                        <Link to="/deals"><Button size="sm" variant="outline" data-testid="goto-deals"><Briefcase size={12} /> Open Deals Pipeline</Button></Link>
                        <Link to="/valuations"><Button size="sm" variant="outline" data-testid="goto-valuations"><Calculator size={12} /> Open Valuations & Dossiers</Button></Link>
                        <Link to="/companies"><Button size="sm" variant="ghost"><Radar size={12} /> Companies list</Button></Link>
                      </div>
                    </div>
                  </div>
                  {discovery.category === "investor_search" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="discovery-results-investors">
                      <thead>
                        <tr className="border-b border-white/5 text-left">
                          <th className="px-3 py-2 overline text-[10px] font-normal">Investor</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Type</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal text-right">AUM</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal text-right">Ticket</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Focus</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Conf.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(discovery.targets || []).map((t, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`discovery-investor-${i}`}>
                            <td className="px-3 py-2.5">
                              <div className="font-medium text-[13px]">{t.name}</div>
                              <div className="text-[10px] text-white/40 max-w-[380px] truncate">{t.description}</div>
                              {t.rationale && <div className="text-[10px] text-gold/80 mt-0.5 italic">→ {t.rationale}</div>}
                            </td>
                            <td className="px-3 py-2.5 text-[11px] text-white/70">{t.investor_type}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-[11px] text-gold">€{((t.aum_eur || 0) / 1e6).toFixed(0)}M</td>
                            <td className="px-3 py-2.5 text-right font-mono text-[11px] text-white/70">€{((t.ticket_min_eur || 0) / 1e6).toFixed(0)}–{((t.ticket_max_eur || 0) / 1e6).toFixed(0)}M</td>
                            <td className="px-3 py-2.5 text-[11px] text-white/60">
                              <div>{t.geo_focus}</div>
                              <div className="text-[10px] text-white/40 truncate max-w-[180px]">{Array.isArray(t.sector_focus) ? t.sector_focus.join(", ") : t.sector_focus}</div>
                            </td>
                            <td className="px-3 py-2.5">
                              <Badge tone={t.confidence === "high" ? "green" : t.confidence === "medium" ? "yellow" : "neutral"}>
                                {t.confidence}
                                {(t._sources || []).length > 1 && <CheckCircle2 size={10} className="ml-0.5" />}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="discovery-results">
                      <thead>
                        <tr className="border-b border-white/5 text-left">
                          <th className="px-3 py-2 overline text-[10px] font-normal">Company</th>
                          {discovery.category && <th className="px-3 py-2 overline text-[10px] font-normal">Signal</th>}
                          <th className="px-3 py-2 overline text-[10px] font-normal">Sector</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Region</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal text-right">Revenue</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Ownership</th>
                          <th className="px-3 py-2 overline text-[10px] font-normal">Conf.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(discovery.targets || []).map((t, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`discovery-target-${i}`}>
                            <td className="px-3 py-2.5">
                              <div className="font-medium text-[13px]">{t.name}</div>
                              <div className="text-[10px] text-white/40 max-w-[380px] truncate">{t.description}</div>
                              {t.rationale && <div className="text-[10px] text-gold/80 mt-0.5 italic">→ {t.rationale}</div>}
                            </td>
                            {discovery.category && (
                              <td className="px-3 py-2.5 text-[11px] text-white/70 max-w-[220px]">
                                <div className="text-white/85">{t.signal || t.succession_signal || "—"}</div>
                                <div className="text-[10px] text-white/40">{t.transaction_type || (t.generation ? `${t.generation} gen · ${t.family_name || ""}` : "")}{t.urgency ? ` · urgency ${t.urgency}` : ""}</div>
                              </td>
                            )}
                            <td className="px-3 py-2.5 text-[11px] text-white/70">{t.sector}</td>
                            <td className="px-3 py-2.5 text-[11px] text-white/60">{t.region || t.country}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-[11px] text-gold">
                              €{((t.revenue_eur || 0) / 1e6).toFixed(0)}M
                            </td>
                            <td className="px-3 py-2.5">
                              <Badge tone={t.ownership?.toLowerCase().includes("family") ? "gold" : t.ownership?.toLowerCase().includes("public") ? "navy" : "neutral"}>
                                {t.ownership}
                              </Badge>
                            </td>
                            <td className="px-3 py-2.5">
                              <Badge tone={t.confidence === "high" ? "green" : t.confidence === "medium" ? "yellow" : "neutral"}>
                                {t.confidence}
                                {(t._sources || []).length > 1 && <CheckCircle2 size={10} className="ml-0.5" />}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </Card>
              )}
              {!discovery && !discovering && (
                <Card>
                  <div className="text-center py-16">
                    <Sparkles size={32} className="mx-auto text-white/20 mb-3" />
                    <div className="text-white/50 mb-2">Deep Discovery with Claude + GPT-5.2 in parallel</div>
                    <div className="text-[11px] text-white/30 max-w-md mx-auto">
                      Specify sector, country and target revenue → CLARA researches real Italian / European companies from each model's training data (annual reports, filed financial statements, press releases) and cross-validates the results.
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {tab === "registro" && (
            <>
              {registro && (
                <Card>
                  <div className="flex items-start gap-3 mb-4">
                    {registro.success ? <Database size={20} className="text-gold mt-0.5 shrink-0" /> : <AlertCircle size={20} className="text-red-400 mt-0.5 shrink-0" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <div className="overline">Registro Imprese</div>
                        {registro.success ? (
                          <>
                            <Badge tone="green">{registro.count} results</Badge>
                            {registro.inserted_count !== undefined && <Badge tone="gold">{registro.inserted_count} companies</Badge>}
                            {registro.deals_created !== undefined && <Badge tone="navy">{registro.deals_created} deals</Badge>}
                            {registro.valuations_queued !== undefined && <Badge tone="green">{registro.valuations_queued} valuations</Badge>}
                          </>
                        ) : <Badge tone="red">Error</Badge>}
                      </div>
                      {registro.inserted_count > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <Link to="/deals"><Button size="sm" variant="outline"><Briefcase size={12} /> Deals</Button></Link>
                          <Link to="/valuations"><Button size="sm" variant="outline"><Calculator size={12} /> Valuations</Button></Link>
                        </div>
                      )}
                      {!registro.success && (
                        <div className="text-[12px] text-white/70 leading-relaxed">
                          <div className="font-medium text-red-300">{registro.error}</div>
                          {registro.hint && <div className="text-white/50 mt-1">{registro.hint}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                  {registro.success && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="registro-results">
                        <thead>
                          <tr className="border-b border-white/5 text-left">
                            <th className="px-3 py-2 overline text-[10px] font-normal">Denominazione</th>
                            <th className="px-3 py-2 overline text-[10px] font-normal">PIVA</th>
                            <th className="px-3 py-2 overline text-[10px] font-normal">ATECO</th>
                            <th className="px-3 py-2 overline text-[10px] font-normal">Sede</th>
                            <th className="px-3 py-2 overline text-[10px] font-normal text-right">Fatturato</th>
                            <th className="px-3 py-2 overline text-[10px] font-normal text-right">Dipendenti</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(registro.results || []).map((r, i) => (
                            <tr key={i} className="border-b border-white/5 last:border-0">
                              <td className="px-3 py-2.5 font-medium">{r.denominazione}</td>
                              <td className="px-3 py-2.5 font-mono text-[10px] text-white/60">{r.piva}</td>
                              <td className="px-3 py-2.5 text-[10px] text-white/60">{r.ateco}</td>
                              <td className="px-3 py-2.5 text-[11px] text-white/70">{r.comune} ({r.provincia})</td>
                              <td className="px-3 py-2.5 text-right font-mono text-[11px] text-gold">{r.fatturato ? fmt.money(r.fatturato) : "—"}</td>
                              <td className="px-3 py-2.5 text-right text-[11px]">{r.dipendenti || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}
              {!registro && !registroLoading && (
                <Card>
                  <div className="text-center py-16">
                    <Database size={32} className="mx-auto text-white/20 mb-3" />
                    <div className="text-white/50 mb-2">Registro Imprese live — Openapi.com</div>
                    <div className="text-[11px] text-white/30 max-w-md mx-auto">
                      Search across every Italian S.p.A. and S.r.l. with filed financial statements. Filter by company name, province (2-letter code), ATECO sector code, revenue and employees. Requires an active Openapi.com key with the "IT-advanced" product enabled.
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {tab === "scan" && (
            <>
              {result?.narrative && (
                <Card>
                  <div className="flex items-start gap-3">
                    <Zap size={18} className="text-gold mt-0.5 shrink-0" />
                    <div>
                      <div className="overline mb-2">CLARA Thesis · Database Scan</div>
                      <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{result.narrative}</div>
                    </div>
                  </div>
                </Card>
              )}

              <Card>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="overline">Ranked Targets</div>
                    <div className="font-display text-xl mt-1">{result?.count || 0} companies matched</div>
                  </div>
                  {result && <Badge tone="gold">{STRATEGIES.find(s => s.id === result.strategy)?.name}</Badge>}
                </div>

                <div className="overflow-x-auto -mx-6 md:-mx-0">
                  <table className="w-full text-sm" data-testid="scout-results-table">
                    <thead>
                      <tr className="border-b border-white/5 text-left">
                        <th className="px-4 py-2 overline font-normal text-[10px]">#</th>
                        <th className="px-4 py-2 overline font-normal text-[10px]">Target</th>
                        <th className="px-4 py-2 overline font-normal text-[10px]">Sector</th>
                        <th className="px-4 py-2 overline font-normal text-[10px] text-right">Revenue</th>
                        <th className="px-4 py-2 overline font-normal text-[10px] text-right">EBITDA</th>
                        <th className="px-4 py-2 overline font-normal text-[10px] text-right">Margin</th>
                        <th className="px-4 py-2 overline font-normal text-[10px]">Ticker</th>
                        <th className="px-4 py-2 overline font-normal text-[10px] text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result?.targets || []).map((t, i) => (
                        <tr key={t.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`scout-target-${t.id}`}>
                          <td className="px-4 py-3 text-white/30 text-[12px] font-mono">{i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{t.name}</div>
                            <div className="text-[11px] text-white/40 truncate max-w-[280px]">{t.description}</div>
                          </td>
                          <td className="px-4 py-3 text-white/60 text-[12px]">{t.sector}</td>
                          <td className="px-4 py-3 text-right font-mono text-[12px]">{fmt.money(t.revenue)}</td>
                          <td className="px-4 py-3 text-right font-mono text-[12px]">{fmt.money(t.ebitda)}</td>
                          <td className="px-4 py-3 text-right text-[12px]">{fmt.pct(t.ebitda_margin)}</td>
                          <td className="px-4 py-3 text-[11px]">
                            {t.ticker ? <span className="font-mono text-[#D4AF37]">{t.ticker}</span> : <span className="text-white/30">Private</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono font-semibold"
                                  style={{ background: t.score > 70 ? "rgba(197,160,89,0.15)" : "rgba(255,255,255,0.05)", color: t.score > 70 ? "#D4AF37" : "#a1a1aa" }}>
                              {t.score}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {!loading && !result?.targets?.length && (
                        <tr><td colSpan={8} className="text-center py-10 text-white/30">No matches. Switch to <strong>AI Deep Discovery</strong> to research new targets.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
